import compact from 'lodash/compact'
import { useParams } from 'react-router'

import {
  useCurrentMembership,
  useUserBuyerCompanyId,
  useUserSellerCompanyId,
} from 'src/contexts/userContext'
import { useBuyerCompanyQuery } from 'src/queries/buyer/buyerCompanies'
import { useMeQuery } from 'src/queries/seller/me'
import { useVerticalsQuery } from 'src/queries/shared'
import { Vertical, VerticalSlugParams } from 'src/types'

import { useIsPublicPage } from './useIsPublicPage'

interface FilterVerticalArgs {
  allowedVerticalIds: Id[]
  defaultVerticalId?: Id | null
}

const filterVerticals =
  ({ allowedVerticalIds, defaultVerticalId }: FilterVerticalArgs) =>
  (verticals: Vertical[]) =>
    compact(
      allowedVerticalIds.map(verticalId => verticals.find(vertical => vertical.id === verticalId))
    ).sort(a => (a.id === defaultVerticalId ? -1 : 1))

export const useCompanyVerticals = () => {
  const isPublicPage = useIsPublicPage()
  const currentMembership = useCurrentMembership({ optional: true })

  const buyerCompanyId = useUserBuyerCompanyId({ optional: isPublicPage })
  const sellerCompanyId = useUserSellerCompanyId({ optional: isPublicPage })

  const { data: seller } = useMeQuery({ enabled: !!sellerCompanyId })
  const { data: buyerCompany } = useBuyerCompanyQuery({ id: buyerCompanyId })

  const allowedVerticalIds =
    (!!sellerCompanyId ? seller?.verticalIds : buyerCompany?.verticalIds) ?? []

  const { data: companyVerticals = [] } = useVerticalsQuery({
    queryOptions: {
      select: filterVerticals({
        allowedVerticalIds,
        defaultVerticalId: currentMembership?.defaultVerticalId,
      }),
      enabled: !isPublicPage,
    },
  })

  return companyVerticals
}

export const useCurrentVertical = () => {
  const { vertical: verticalSlug } = useParams<VerticalSlugParams>()
  const verticals = useCompanyVerticals()
  const currentVertical = verticalSlug
    ? verticals.find(vertical => vertical.slug === verticalSlug)
    : verticals[0]

  return currentVertical
}
